import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Captions, Trash2, Pencil, Check, X, ImagePlus } from "lucide-react";
import { api, errMsg, resolveMedia } from "@/lib/api";
import { SUB_LANGS } from "@/lib/subLangs";

const EpisodeRow = ({ ep, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(ep.title);
  const [number, setNumber] = useState(ep.number);
  const [season, setSeason] = useState(ep.season || 1);
  const [lang, setLang] = useState("ur");
  const [busy, setBusy] = useState(false);

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const save = () => run(() => api.put(`/episodes/${ep.id}`, { title, number: Number(number), season: Number(season) }), "Episode updated").then(() => setEditing(false));
  const addSub = (file) => file && run(() => { const fd = new FormData(); fd.append("subtitle", file); fd.append("lang", lang); return api.post(`/episodes/${ep.id}/subtitle`, fd); }, `${SUB_LANGS.find((l) => l.code === lang)?.label} subtitles added`);
  const removeSub = (s) => window.confirm(`Remove ${s.label} subtitles?`) && run(() => api.delete(`/episodes/${ep.id}/subtitle/${s.id}`), "Subtitle removed");
  const removeEp = () => window.confirm(`Delete episode ${ep.number}?`) && run(() => api.delete(`/episodes/${ep.id}`), "Episode deleted");
  const addThumb = (file) => file && run(() => { const fd = new FormData(); fd.append("thumbnail", file); return api.post(`/episodes/${ep.id}/thumbnail`, fd); }, "Thumbnail updated");

  return (
    <div data-testid={`admin-episode-row-${ep.id}`} className={`py-3 flex flex-col gap-2 ${busy ? "opacity-60" : ""}`}>
      <div className="flex items-center gap-3 text-sm">
        {editing ? (
          <>
            <input data-testid={`episode-edit-season-${ep.id}`} type="number" min="1" value={season} onChange={(e) => setSeason(e.target.value)} className="kp-input !py-1.5 !px-2 w-14 text-xs" title="Season" />
            <input data-testid={`episode-edit-number-${ep.id}`} type="number" min="1" value={number} onChange={(e) => setNumber(e.target.value)} className="kp-input !py-1.5 !px-2 w-16 text-xs" />
            <input data-testid={`episode-edit-title-${ep.id}`} value={title} onChange={(e) => setTitle(e.target.value)} className="kp-input !py-1.5 !px-2 flex-1 text-sm" placeholder="Episode title" />
            <button data-testid={`episode-edit-save-${ep.id}`} onClick={save} className="p-1.5 text-[#00F0FF]" aria-label="Save"><Check size={15} /></button>
            <button data-testid={`episode-edit-cancel-${ep.id}`} onClick={() => { setEditing(false); setTitle(ep.title); setNumber(ep.number); setSeason(ep.season || 1); }} className="p-1.5 text-[#94A3B8]" aria-label="Cancel"><X size={15} /></button>
          </>
        ) : (
          <>
            <span className="font-mono text-xs text-[#64748B] w-14">S{ep.season || 1} E{String(ep.number).padStart(2, "0")}</span>
            <label className="relative w-14 h-8 shrink-0 cursor-pointer group/thumb bg-white/[0.04] border border-white/[0.08] overflow-hidden" title="Thumbnail upload">
              {ep.thumbnail_url ? <img src={resolveMedia(ep.thumbnail_url)} alt="" className="w-full h-full object-cover" /> : <ImagePlus size={13} className="absolute inset-0 m-auto text-[#64748B]" />}
              <span className="absolute inset-0 bg-[#08070C]/70 opacity-0 group-hover/thumb:opacity-100 flex items-center justify-center transition-opacity"><ImagePlus size={13} className="text-[#00F0FF]" /></span>
              <input data-testid={`thumbnail-file-input-${ep.id}`} type="file" accept="image/*" className="hidden" onChange={(e) => { addThumb(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
            <span className="flex-1 truncate">{ep.title || `Episode ${ep.number}`}</span>
            <button data-testid={`episode-edit-button-${ep.id}`} onClick={() => setEditing(true)} className="p-1.5 text-[#94A3B8] hover:text-[#00F0FF] transition-colors" aria-label="Edit"><Pencil size={14} /></button>
            <button data-testid={`admin-episode-delete-${ep.id}`} onClick={removeEp} className="p-1.5 text-[#94A3B8] hover:text-[#FF2A55] transition-colors" aria-label="Delete"><Trash2 size={14} /></button>
          </>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap pl-16">
        {ep.subtitles?.map((s) => (
          <span key={s.id} data-testid={`subtitle-chip-${ep.id}-${s.lang}`} className="flex items-center gap-1.5 text-[0.62rem] font-mono text-[#00F0FF] border border-[#00F0FF]/40 pl-2 pr-1 py-0.5">
            CC {s.label}
            <button data-testid={`subtitle-remove-${ep.id}-${s.lang}`} onClick={() => removeSub(s)} className="p-0.5 hover:text-[#FF2A55] transition-colors" aria-label={`Remove ${s.label}`}><X size={11} /></button>
          </span>
        ))}
        <select data-testid={`subtitle-lang-select-${ep.id}`} value={lang} onChange={(e) => setLang(e.target.value)} className="kp-input !py-1 !px-2 !w-auto text-[0.68rem]">
          {SUB_LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
        </select>
        <label className="kp-pill cursor-pointer flex items-center gap-1.5 !py-1">
          <Captions size={12} /> Add .vtt
          <input data-testid={`subtitle-file-input-${ep.id}`} type="file" accept=".vtt,text/vtt" className="hidden" onChange={(e) => { addSub(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
      </div>
    </div>
  );
};

export const EpisodeRows = ({ animeId, refreshKey, onChanged }) => {
  const [eps, setEps] = useState([]);
  useEffect(() => {
    if (!animeId) return setEps([]);
    api.get(`/anime/${animeId}`).then((r) => setEps(r.data.episodes));
  }, [animeId, refreshKey]);

  if (!animeId || !eps.length) return null;
  return (
    <div data-testid="admin-episode-rows" className="border-t border-white/[0.06] pt-4 flex flex-col divide-y divide-white/[0.06]">
      <span className="kp-eyebrow !text-[0.58rem] pb-2">Uploaded episodes · click the box to set a thumbnail, edit titles, add/remove subtitles</span>
      {eps.map((ep) => <EpisodeRow key={ep.id} ep={ep} onChanged={onChanged} />)}
    </div>
  );
};
